import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import ConfirmDialog from "../components/ui/ConfirmDialog";
import { Calendar } from "@/components/ui/calendar";
import { useGlobalLoader } from "@/context/GlobalLoaderContext";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Send,
  Mail,
  Eye,
  DollarSign,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  Wifi,
  WifiOff,
  Image,
  Upload,
  FileText,
  Download,
  RefreshCw,
  WifiSync,
  Plus,
  Save,
  Edit,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, API_FOR_IMG } from "../App";

export default function AdminPayments() {
  const [clients, setClients] = useState([]);
  const [soas, setSoas] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const { showLoader, hideLoader, showSuccess } = useGlobalLoader();
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [connectionFilter, setConnectionFilter] = useState("all");
  const [balanceFilter, setBalanceFilter] = useState("all");
  const [viewDialog, setViewDialog] = useState(false);
  const [billingDetails, setBillingDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [adjustmentButtonSaved, setAdjustmentButtonSaved] = useState(false);
  const [numDays, setNumDays] = useState("0");
  const [numDaysOld, setNumDaysOld] = useState("0");
  const [paymentData, setPaymentData] = useState({
    client_id: "",
    amount: "",
    payment_method: "cash",
    reference_number: "",
    notes: "",
  });
  const [proofDialog, setProofDialog] = useState(false);
  const [proofImage, setProofImage] = useState(null);
  const [proofClientName, setProofClientName] = useState("");
  const [dateOpen, setDateOpen] = useState(false);

  const [dueDateFilter, setDueDateFilter] = useState("all");
  const [dueDateFrom, setDueDateFrom] = useState("");
  const [dueDateTo, setDueDateTo] = useState("");

  const [reconnectDialog, setReconnectDialog] = useState({
    open: false,
    client: null,
    extension_date: "",
  });

  // Date formatting helper - MMM-dd-yyyy format (e.g., Jan-01-2026)
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    try {
      // If already in MMM-dd-yyyy format, return as is
      if (/^[A-Z][a-z]{2}-\d{2}-\d{4}/.test(dateStr))
        return dateStr.split(" ")[0];
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const month = months[date.getMonth()];
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      return `${month}-${day}-${year}`;
    } catch {
      return dateStr;
    }
  };

  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [start, setStart] = useState({ x: 0, y: 0 });

  const zoomIn = () => setZoom((z) => Math.min(z + 0.2, 5));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.2, 1));
  const resetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  const handleMouseDown = (e) => {
    setDragging(true);
    setStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPosition({
      x: e.clientX - start.x,
      y: e.clientY - start.y,
    });
  };

  const handleMouseUp = () => setDragging(false);

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const [clientsRes, soasRes] = await Promise.all([
        axios.get(`${API}/clients-payments`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/soa`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setClients(clientsRes.data);
      setSoas(soasRes.data);
    } catch (error) {
      toast.error("Failed to fetch clients");
    } finally {
      setLoading(false);
    }
  };

  const getClientsPaymentReference = async (clientId, maxAmount) => {
    try {
      const token = localStorage.getItem("token");

      const latestSOA = getClientLatestSOA(clientId);

      const paymentReferenceRes = await axios.get(
        `${API}/payments/reference-numbers/${clientId}?soa_number=${latestSOA.soa_number}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      console.log("Payment Reference Response:", paymentReferenceRes.data);

      const paymentReferenceData = paymentReferenceRes.data;
      if (paymentReferenceData && paymentReferenceData.length > 0) {
        setPaymentData({
          client_id: clientId,
          amount: maxAmount.toString(),
          payment_method: paymentReferenceData[0].payment_method,
          reference_number: paymentReferenceData[0].reference_number,
          notes: "",
          max_amount: maxAmount, // Store for validation
        });
      } else {
        setPaymentData({
          client_id: clientId,
          amount: maxAmount.toString(),
          payment_method: "cash",
          reference_number: "",
          notes: "",
          max_amount: maxAmount, // Store for validation
        });
      }
    } catch (error) {
      toast.error("Failed to fetch clients payment reference");
    }
  };

  const handlesetNumDays = (value) => {
    let varNumDays = parseInt(value) || 0;
    let TotalAdjustment = (billingDetails.current_charges / 30) * varNumDays;
    let newTotalAdjustment = TotalAdjustment;
    let newoutstanding_balance = 0;

    if (newTotalAdjustment >= billingDetails.current_charges) {
      newoutstanding_balance = billingDetails.previous_balance;
    } else {
      // 2 decimal places
      newoutstanding_balance = parseFloat(
        billingDetails.current_charges +
          billingDetails.previous_balance -
          newTotalAdjustment,
      ).toFixed(2);
    }

    console.log("Current Charges:", billingDetails.current_charges);
    console.log("Num Days:", varNumDays);
    console.log("Total Adjustment:", TotalAdjustment);
    console.log("New Total Adjustment:", newTotalAdjustment);
    console.log("Outstanding Balance:", newoutstanding_balance);

    setBillingDetails((prev) => ({
      ...prev,
      total_adjustment: newTotalAdjustment,
      outstanding_balance: newoutstanding_balance,
      numDays: varNumDays,
    }));
    setNumDays(varNumDays);
  };

  const handleAdjustmentButtonSaved = async (clientId, value) => {
    if (value) {
      if (billingDetails.outstanding_balance === 0) {
        toast.error("This client marked as fully paid. No adjustment needed.");
        return;
      }
      setNumDaysOld(numDays);
      setAdjustmentButtonSaved(value);
      return;
    }

    try {
      const token = localStorage.getItem("token");

      const data = {
        client_id: clientId,
        soa_number: billingDetails.soa_number,
        total_adjustment: billingDetails.total_adjustment || 0,
        outstanding_balance: billingDetails.outstanding_balance || 0,
        numDays: billingDetails.numDays || 0,
      };

      await axios.put(`${API}/update-client-balance/${clientId}`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("SOA adjustment saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update SOA");
    }

    setAdjustmentButtonSaved(value);
    fetchClients();
    viewBillingDetails(clientId);
  };

  // Check if client has any SOA
  const clientHasSOA = (clientId) => {
    return soas.some((soa) => soa.client_id === clientId);
  };

  // Get client's latest SOA
  const getClientLatestSOA = (clientId) => {
    const clientSOAs = soas.filter((soa) => soa.client_id === clientId);
    if (clientSOAs.length === 0) return null;
    return clientSOAs.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    )[0];
  };

  // Download SOA PDF
  const downloadSOAPDF = async (soaId, soaNumber) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/soa/${soaId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `SOA_${soaNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("PDF downloaded successfully");
    } catch (error) {
      toast.error("Failed to download PDF");
    }
  };

  // Resend SOA email
  const resendSOAEmail = async (soaId) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/soa/${soaId}/resend-email`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success("SOA email sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleRefresh = () => {
    fetchClients();
  };

  // Generate SOA for client
  const generateSOA = async (clientId) => {
    // if (billingDetails.outstanding_balance === 0) {
    //   toast.error("This client marked as fully paid. No adjustment needed.");
    //   return;
    // }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/soa/generate/${clientId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      toast.success(`SOA ${response.data.soa_number} generated successfully`);
      fetchClients(); // Refresh data
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate SOA");
    }
  };

  // Check if client is fully paid (zero balance)
  const clientIsFullyPaid = (client) => {
    return (client.balance || 0) === 0 && client.status === "Paid";
  };

  // Can client make payment?
  const canMakePayment = (client) => {
    // Rule 1: Cannot pay if no SOA generated
    if (!clientHasSOA(client.id)) {
      return { canPay: false, reason: "No SOA generated" };
    }
    // Rule 2: Cannot pay if already fully paid (zero balance)
    if (clientIsFullyPaid(client)) {
      return { canPay: false, reason: "Already fully paid" };
    }
    return { canPay: true, reason: null };
  };

  // const filteredClients = clients.filter((client) => {
  //   const matchesSearch =
  //     client.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     client.account_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     client.email.toLowerCase().includes(searchQuery.toLowerCase());

  //   const matchesStatus =
  //     statusFilter === "all" || client.status === statusFilter;

  //   // Connection filter (Online/Disconnected)
  //   let matchesConnection = true;
  //   if (connectionFilter === "online") {
  //     matchesConnection = !client.is_disconnected;
  //   } else if (connectionFilter === "disconnected") {
  //     matchesConnection = client.is_disconnected;
  //   }

  //   // Balance filter
  //   let matchesBalance = true;
  //   if (balanceFilter === "with_balance") {
  //     matchesBalance = (client.balance || 0) > 0;
  //   } else if (balanceFilter === "fully_paid") {
  //     matchesBalance = client.status === "Paid" && (client.balance || 0) === 0;
  //   }

  //   return (
  //     matchesSearch && matchesStatus && matchesConnection && matchesBalance
  //   );
  // });

  const filteredClients = clients.filter((client) => {
    const search = searchQuery.toLowerCase();

    const matchesSearch =
      (client.full_name || "").toLowerCase().includes(search) ||
      (client.account_number || "").toLowerCase().includes(search) ||
      (client.email || "").toLowerCase().includes(search);

    const matchesStatus =
      statusFilter === "all" || client.status === statusFilter;

    let matchesConnection = true;

    if (connectionFilter === "online") {
      matchesConnection = !client.is_disconnected;
    } else if (connectionFilter === "disconnected") {
      matchesConnection = client.is_disconnected;
    }

    let matchesBalance = true;

    if (balanceFilter === "with_balance") {
      matchesBalance = (client.balance || 0) > 0;
    } else if (balanceFilter === "fully_paid") {
      matchesBalance = client.status === "Paid" && (client.balance || 0) === 0;
    }

    // Normalize due dates to YYYY-MM-DD
    const normalizeDate = (value) => {
      if (!value) return "";

      // Handles ISO dates and YYYY-MM-DD strings
      const isoMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (isoMatch) {
        return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
      }

      // Handles dates such as Apr-24-2026
      const parsed = new Date(value);

      if (Number.isNaN(parsed.getTime())) return "";

      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");

      return `${year}-${month}-${day}`;
    };

    const dueDate = normalizeDate(client.due_date);

    // Local date, avoiding UTC date shifts
    const todayDate = new Date();
    const today = [
      todayDate.getFullYear(),
      String(todayDate.getMonth() + 1).padStart(2, "0"),
      String(todayDate.getDate()).padStart(2, "0"),
    ].join("-");

    let matchesDueDate = true;

    if (dueDateFilter === "overdue") {
      matchesDueDate = Boolean(dueDate && dueDate < today);
    } else if (dueDateFilter === "today") {
      matchesDueDate = dueDate === today;
    } else if (dueDateFilter === "upcoming") {
      const nextSevenDays = new Date(todayDate);
      nextSevenDays.setDate(nextSevenDays.getDate() + 7);

      const endDate = [
        nextSevenDays.getFullYear(),
        String(nextSevenDays.getMonth() + 1).padStart(2, "0"),
        String(nextSevenDays.getDate()).padStart(2, "0"),
      ].join("-");

      matchesDueDate = Boolean(
        dueDate && dueDate >= today && dueDate <= endDate,
      );
    } else if (dueDateFilter === "custom") {
      matchesDueDate = Boolean(
        dueDate &&
        (!dueDateFrom || dueDate >= dueDateFrom) &&
        (!dueDateTo || dueDate <= dueDateTo),
      );
    } else if (dueDateFilter === "no_date") {
      matchesDueDate = !dueDate;
    }

    return (
      matchesSearch &&
      matchesStatus &&
      matchesConnection &&
      matchesBalance &&
      matchesDueDate
    );
  });

  const toggleClient = (clientId) => {
    setSelectedClients((prev) =>
      prev.includes(clientId)
        ? prev.filter((id) => id !== clientId)
        : [...prev, clientId],
    );
  };

  const toggleAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map((c) => c.id));
    }
  };

  const sendReminders = async () => {
    if (selectedClients.length === 0) {
      toast.error("Please select at least one client");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/payments/send-reminder`,
        { client_ids: selectedClients },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Payment reminders sent to ${selectedClients.length} client(s)`,
      );
      setSelectedClients([]);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const sendRemindersByClient = async (client_id, client_name) => {
    setSending(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/payments/send-reminder`,
        { client_ids: [client_id] },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Payment reminders sent to ${client_name}.`);
      setSelectedClients([]);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const sendToUnpaid = async () => {
    const unpaidIds = clients
      .filter((c) => c.status === "Unpaid" || c.status === "Disconnected")
      .map((c) => c.id);
    if (unpaidIds.length === 0) {
      toast.error("No unpaid clients found");
      return;
    }

    setSending(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/payments/send-reminder`,
        { client_ids: unpaidIds },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        `Payment reminders sent to ${unpaidIds.length} unpaid client(s)`,
      );
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSending(false);
    }
  };

  const viewBillingDetails = async (clientId) => {
    setLoadingDetails(true);
    setViewDialog(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${API}/payments/billing-details/${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      console.log("Billing Details:", response.data);
      setBillingDetails(response.data);
      setNumDays(response.data.numDays || "0");
    } catch (error) {
      toast.error("Failed to fetch billing details");
    } finally {
      setLoadingDetails(false);
    }
  };

  // Dialog state
  const [dialog, setDialog] = React.useState({
    show: false,
    action: "", // "reconnect", "delete", etc.
    client: null,
  });

  // Open dialog for a row
  const openDialog = (client, action) => {
    setDialog({
      show: true,
      action,
      client,
    });
  };

  // Handle confirm
  const handleConfirm = async () => {
    if (!dialog.client) return;

    try {
      const token = localStorage.getItem("token");

      if (dialog.action === "send_reminders") {
        showLoader("Sending payment reminders...");
        sendRemindersByClient(dialog.client.id, dialog.client.full_name);
        showSuccess(`Payment reminder sent to ${dialog.client.full_name}`);
      }

      if (dialog.action === "delete") {
        showLoader("Deleting client...");
        await axios.delete(`${API}/clients/${dialog.client.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // hideLoader();
        showSuccess("Client deleted successfully");
      }

      if (dialog.action === "reconnect") {
        showLoader("Reconnecting client...");
        await axios.post(
          `${API}/mikrotik/unblock-client/${dialog.client.id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // hideLoader();
        showSuccess("Client reconnected successfully");
      }

      if (dialog.action === "disconnect") {
        showLoader("Disconnecting client...");
        await axios.post(
          `${API}/mikrotik/block-client/${dialog.client.id}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        // hideLoader();
        showSuccess("Client disconnected successfully");
      }

      fetchClients();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Action failed");
    }

    setDialog({ show: false, action: "", client: null });
  };

  // const markAsPaid = async (clientId) => {
  //   try {
  //     const token = localStorage.getItem("token");
  //     const response = await axios.post(
  //       `${API}/payments/mark-paid/${clientId}`,
  //       {},
  //       {
  //         headers: { Authorization: `Bearer ${token}` },
  //       },
  //     );
  //     toast.success(response.data.message || "Client marked as paid");
  //     fetchClients();
  //     if (viewDialog) {
  //       viewBillingDetails(clientId);
  //     }
  //   } catch (error) {
  //     toast.error(error.response?.data?.detail || "Failed to mark as paid");
  //   }
  // };

  const openPaymentDialog = async (client) => {
    const paymentCheck = canMakePayment(client);
    if (!paymentCheck.canPay) {
      toast.error(paymentCheck.reason);
      return;
    }

    // Prevent overpayment - set max amount to current balance
    const maxAmount = client.balance || client.monthly_fee || 0;

    await getClientsPaymentReference(client.id, maxAmount);

    // setPaymentData({
    //   client_id: client.id,
    //   amount: maxAmount.toString(),
    //   payment_method: "cash",
    //   reference_number: "",
    //   notes: "",
    //   max_amount: maxAmount, // Store for validation
    // });
    setPaymentDialog(true);
  };

  const recordPayment = async (e) => {
    e.preventDefault();

    // Prevent overpayment
    const amount = parseFloat(paymentData.amount);
    const notes = paymentData.notes
      ? paymentData.notes.trim()
      : "Thank you for your payment!";
    // const maxAmount = paymentData.max_amount || Infinity;

    // if (amount > maxAmount) {
    //   toast.error(
    //     `Payment amount cannot exceed balance of ₱${maxAmount.toLocaleString()}`,
    //   );
    //   return;
    // }

    if (amount <= 0) {
      toast.error("Payment amount must be greater than 0");
      return;
    }

    showLoader("Recording payment...");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/payments`,
        {
          client_id: paymentData.client_id,
          amount: amount,
          payment_method: paymentData.payment_method,
          reference_number: paymentData.reference_number,
          notes: notes,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      // hideLoader();
      // toast.success("Payment recorded successfully");

      setPaymentDialog(false);
      setPaymentData({
        client_id: "",
        amount: "",
        payment_method: "cash",
        reference_number: "",
        notes: "",
      });
      fetchClients();
      showSuccess("Payment recorded successfully");
    } catch (error) {
      hideLoader();
      toast.error(error.response?.data?.detail || "Failed to record payment");
    }
  };

  const viewPaymentProof = (client) => {
    if (client.payment_proof_url) {
      setProofImage(client.payment_proof_url);
      setProofClientName(client.full_name);
      setProofDialog(true);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      Paid: "bg-emerald-100 text-emerald-700 border-emerald-200",
      Unpaid: "bg-red-100 text-red-700 border-red-200",
      Disconnected: "bg-slate-100 text-slate-700 border-slate-200",
      Extended: "bg-blue-100 text-blue-700 border-blue-200",
      New: "bg-green-100 text-green-700 border-green-200",
    };
    return (
      <span
        className={`px-3 py-1 text-xs font-semibold border rounded-full ${colors[status] || colors.Paid}`}
      >
        {status}
      </span>
    );
  };

  // if (loading) {
  //   return (
  //     <AdminLayout>
  //       <div className="flex items-center justify-center h-screen">
  //         Loading...
  //       </div>
  //     </AdminLayout>
  //   );
  // }

  // Calculate total balance from clients
  // const totalBalance = clients
  //   .reduce((sum, client) => {
  //     return sum + (client.balance || 0);
  //   }, 0)
  //   .toLocaleString(undefined, {
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2,
  //   });

  const totalBalance = filteredClients
    .reduce((total, client) => {
      return total + (Number(client.balance) || 0);
    }, 0)
    .toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const client_payment_info = paymentData.client_id
    ? clients.find((c) => c.id === paymentData.client_id)
    : null;

  return (
    <AdminLayout>
      <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden p-4 sm:p-6 lg:p-8">
        <div className="flex items-center justify-between mb-8 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Payment Management
            </h1>
            <p className="text-slate-600 mt-1 mb-2">
              Track payments, send reminders, and manage billing
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={sendToUnpaid}
              disabled={sending}
              variant="outline"
              className="gap-2"
            >
              <Mail className="w-4 h-4" />
              Send to All Unpaid
            </Button>
            <Button
              onClick={sendReminders}
              disabled={sending || selectedClients.length === 0}
              className="bg-blue-600 hover:bg-blue-700 gap-2"
            >
              <Send className="w-4 h-4" />
              Send to Selected ({selectedClients.length})
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, account number, or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[170px]">
            {/* <Label htmlFor="due-date-filter">Due Date Filter</Label> */}

            <Select value={dueDateFilter} onValueChange={setDueDateFilter}>
              <SelectTrigger id="due-date-filter" className="bg-white">
                <SelectValue placeholder="Filter by due date" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all">All Due Dates</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
                <SelectItem value="today">Due Today</SelectItem>
                <SelectItem value="upcoming">Due in 7 Days</SelectItem>
                <SelectItem value="custom">Custom Date Range</SelectItem>
                <SelectItem value="no_date">No Due Date</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {dueDateFilter === "custom" && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1">
                <Label className="block lg:hidden" htmlFor="due-date-from">
                  From
                </Label>
                <Input
                  id="due-date-from"
                  type="date"
                  value={dueDateFrom}
                  onChange={(e) => setDueDateFrom(e.target.value)}
                />
              </div>

              <div className="flex flex-col gap-1">
                <Label className="block lg:hidden" htmlFor="due-date-to">
                  To
                </Label>
                <Input
                  id="due-date-to"
                  type="date"
                  min={dueDateFrom || undefined}
                  value={dueDateTo}
                  onChange={(e) => setDueDateTo(e.target.value)}
                />
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setDueDateFrom("");
                  setDueDateTo("");
                  setDueDateFilter("all");
                }}
              >
                Clear
              </Button>
            </div>
          )}

          {/* Refresh Button */}
          <Button
            onClick={handleRefresh}
            className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg shadow-blue-600/30"
          >
            Refresh
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <div className="flex gap-2 flex-wrap">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[130px] rounded-lg bg-white text-sm">
                <Filter className="w-4 h-4 mr-1" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {/* <SelectItem value="Paid">Paid</SelectItem> */}
                <SelectItem value="Unpaid">Unpaid</SelectItem>
                <SelectItem value="Disconnected">Disconnected</SelectItem>
                <SelectItem value="Extended">Extended</SelectItem>
                <SelectItem value="New">New</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={connectionFilter}
              onValueChange={setConnectionFilter}
            >
              <SelectTrigger className="w-[130px] rounded-lg bg-white text-sm">
                <SelectValue placeholder="Connection" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Connections</SelectItem>
                <SelectItem value="online">Online</SelectItem>
                <SelectItem value="disconnected">Disconnected</SelectItem>
              </SelectContent>
            </Select>
            {/* <Select value={balanceFilter} onValueChange={setBalanceFilter}>
              <SelectTrigger className="w-[130px] rounded-lg bg-white text-sm">
                <SelectValue placeholder="Balance" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Balances</SelectItem>
                <SelectItem value="with_balance">With Balance</SelectItem>
                <SelectItem value="fully_paid">Fully Paid</SelectItem>
              </SelectContent>
            </Select> */}
          </div>
        </div>

        {/* Payment Table */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col h-[calc(200vh-220px)]">
          <div className="overflow-auto flex-1">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200 sticky top-0 z-10">
                <tr>
                  <th className="px-6 py-4 text-left">
                    <Checkbox
                      checked={
                        selectedClients.length === filteredClients.length &&
                        filteredClients.length > 0
                      }
                      onCheckedChange={toggleAll}
                    />
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase">
                    Client Name
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase hidden sm:table-cell md:table-cell">
                    Plan
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase hidden sm:table-cell md:table-cell">
                    Balance
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase hidden sm:table-cell md:table-cell">
                    Due Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-600 uppercase hidden sm:table-cell md:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Client ACC Status (MT)
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider hidden sm:table-cell md:table-cell">
                    Blocked (No Payment) (MT)
                  </th>
                  <th className="px-6 py-4 text-left sm:text-right text-xs font-semibold text-slate-600 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-10 text-center text-slate-500"
                    >
                      Loading...
                    </td>
                  </tr>
                ) : filteredClients?.length === 0 ? (
                  <tr>
                    <td
                      colSpan="9"
                      className="px-6 py-12 text-center text-slate-500"
                    >
                      No clients found
                    </td>
                  </tr>
                ) : (
                  filteredClients.map((client) => (
                    <tr key={client.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4">
                        <Checkbox
                          checked={selectedClients.includes(client.id)}
                          onCheckedChange={() => toggleClient(client.id)}
                        />
                      </td>
                      <td className="px-6 py-3">
                        <p className="text-xs font-mono text-slate-500">
                          {client.account_number}
                        </p>
                        <p className="text-sm font-semibold text-slate-900">
                          {client.full_name}
                        </p>
                        <p className="text-xs font-mono text-slate-500 hidden sm:table-cell md:table-cell">
                          {client.email}
                        </p>
                        <p className="text-xs text-slate-500 sm:hidden">
                          Balance: ₱{parseFloat(client.balance).toFixed(2)}
                        </p>
                        <p className="text-xs text-slate-500 sm:hidden">
                          Installed: {<br />}
                          {formatDate(client.installation_date)}
                        </p>
                        <p className="text-xs text-slate-500 sm:hidden">
                          DueDate:{<br />} {formatDate(client.due_date)}
                        </p>
                        <p className="text-xs text-slate-500 sm:hidden">
                          Disconnection Date:{" "}
                          {formatDate(client.disconnection_date)}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 hidden sm:table-cell md:table-cell">
                        {client.plan_name}
                        {client.status === "New" &&
                          client.installation_date && (
                            <p className="text-xs text-slate-500">
                              Installed: {formatDate(client.installation_date)}
                            </p>
                          )}
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell md:table-cell">
                        <span
                          className={`text-sm font-bold ${(client.balance || 0) > 0 ? "text-red-600" : "text-emerald-600"}`}
                        >
                          ₱{(client.balance || 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 hidden sm:table-cell md:table-cell w-[140px]">
                        {formatDate(client.due_date)}
                        <p className="text-xs text-slate-500">
                          Disconn Date: {<br />}
                          {formatDate(client.disconnection_date)}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-sm text-slate-600 hidden sm:table-cell md:table-cell">
                        {getStatusBadge(client.status)}
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell md:table-cell">
                        {client.pppoe_active ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm">
                            Connected
                          </span>
                        ) : client.pppoe_ip === "No MikroTik Connection" ? (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            No MikroTik Connection
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            Disconnected
                          </span>
                        )}
                        <p className="text-xs font-mono text-slate-500">
                          {client.pppoe_ip}
                        </p>
                      </td>
                      <td className="px-6 py-4 hidden sm:table-cell md:table-cell">
                        {client.firewall_disabled ? (
                          <span className="flex items-center gap-1 text-emerald-600 text-sm">
                            Connected
                          </span>
                        ) : client.firewall_list === null ? (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            No Firewall List
                          </span>
                        ) : client.firewall_list ===
                          "No MikroTik Connection" ? (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            No MikroTik Connection
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 text-sm">
                            Disconnected
                          </span>
                        )}
                      </td>
                      <td className="flex px-6 py-4 flex-wrap justify-start sm:justify-end gap-1 sm:gap-2">
                        {client.firewall_disabled !== true &&
                          client.firewall_list !== null &&
                          client.firewall_list !== "No MikroTik Connection" && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                setReconnectDialog({
                                  open: true,
                                  client,
                                  extension_date: "",
                                })
                              }
                              className="hover:bg-orange-50 text-orange-600 hover:text-orange-600"
                              title="Reconnect"
                              data-testid={`add-area-btn-${client.id}`}
                            >
                              <WifiSync className="w-2 h-2" />
                              Reconnect
                            </Button>
                          )}
                        {client.firewall_disabled === true && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openDialog(client, "disconnect")}
                            className="hover:bg-red-50 text-red-600 hover:text-red-600"
                            title="Disconnect"
                            data-testid={`add-area-btn-${client.id}`}
                          >
                            <WifiOff className="w-2 h-2" />
                            Disconnect
                          </Button>
                        )}
                        {client.payment_proof_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => viewPaymentProof(client)}
                            className="hover:bg-amber-50 text-amber-600 hover:text-amber-600"
                            title="View Payment Proof"
                            data-testid={`view-proof-${client.id}`}
                          >
                            <Image className="w-4 h-4 mr-1" />
                            View Payment Proof
                          </Button>
                        )}
                        {(() => {
                          const paymentCheck = canMakePayment(client);
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() =>
                                openDialog(client, "send_reminders")
                              }
                              className={
                                paymentCheck.canPay
                                  ? "hover:bg-blue-50 text-blue-600 hover:text-blue-600"
                                  : "opacity-50 cursor-not-allowed text-slate-400"
                              }
                              disabled={!paymentCheck.canPay}
                              data-testid={`view-billing-${client.id}`}
                            >
                              <Mail className="w-4 h-4 mr-1" />
                              Send Reminder
                            </Button>
                          );
                        })()}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => viewBillingDetails(client.id)}
                          className="hover:bg-green-50 text-green-600 hover:text-green-600"
                          data-testid={`view-billing-${client.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View Billing Details
                        </Button>
                        {(() => {
                          const paymentCheck = canMakePayment(client);
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openPaymentDialog(client)}
                              className={
                                paymentCheck.canPay
                                  ? "hover:bg-emerald-50 hover:text-emerald-600"
                                  : "opacity-50 cursor-not-allowed text-slate-400"
                              }
                              disabled={!paymentCheck.canPay}
                              title={paymentCheck.reason || "Record Payment"}
                              data-testid={`pay-${client.id}`}
                            >
                              <DollarSign className="w-4 h-4 mr-1" />
                              Record Payment
                            </Button>
                          );
                        })()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Results count */}

        <div className="mt-4 text-sm text-slate-600 block">
          {/* Showing {clients.length} clients{" "} */}
          Showing {filteredClients.length} of {clients.length} clients{" "}
          <span className="text-red-700 font-bold">unpaid</span>,{" "}
          <span className="text-slate-700 font-bold">
            {clients.filter((c) => c.status === "Disconnected").length}{" "}
            disconnected.
          </span>
        </div>

        <div className="mt-2 text-sm text-slate-600 block">
          {/* For Total Balance, sum all clients' balances and format as currency */}
          <span className="text-red-700 font-bold text-lg">
            Total Balance: ₱ {totalBalance}
          </span>
        </div>
        {/* View Billing Details Dialog */}
        <Dialog open={viewDialog} onOpenChange={setViewDialog}>
          <DialogContent className="sm:max-w-[600px] p-0 overflow-hidden">
            <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
              <DialogTitle>Billing Details</DialogTitle>
              {/* <apre>{JSON.stringify(billingDetails)}</apre> */}
            </DialogHeader>
            <div className="max-h-[calc(90vh-80px)] overflow-y-auto px-6 pb-6">
              {loadingDetails ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : (
                billingDetails && (
                  <div className="space-y-6">
                    {/* Client Info */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <h3 className="font-semibold text-slate-900 mb-2">
                        {billingDetails.client.full_name}
                      </h3>
                      <p className="text-sm text-slate-600">
                        Account: {billingDetails.client.account_number}
                      </p>
                      <p className="text-sm text-slate-600">
                        Plan: {billingDetails.client.plan_name} - ₱
                        {billingDetails.client.monthly_fee}/mo
                      </p>
                      <p className="text-sm text-slate-600">
                        Email: {billingDetails.client.email}
                      </p>
                      <p className="text-sm text-slate-600">
                        Installation Date:{" "}
                        {formatDate(billingDetails.client.installation_date)}
                      </p>
                    </div>

                    {/* Balance Summary */}
                    <div className="grid grid-cols-2 gap-4">
                      {/* <pre>{JSON.stringify(billingDetails)} </pre> */}
                      <div className="bg-blue-50 rounded-lg p-4 text-center">
                        <p className="text-xs sm:text-sm text-blue-600">
                          Current Charges
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-blue-700">
                          ₱
                          {(billingDetails.client.status !== "Paid"
                            ? parseFloat(
                                billingDetails.current_charges,
                              ).toFixed(2)
                            : 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-amber-50 rounded-lg p-4 text-center">
                        <p className="text-xs sm:text-sm text-amber-600">
                          Previous Balance
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-amber-700">
                          {/* ₱{billingDetails.previous_balance?.toLocaleString()} */}
                          ₱
                          {(billingDetails.client.status !== "Paid"
                            ? parseFloat(
                                billingDetails.previous_balance,
                              ).toFixed(2)
                            : 0
                          ).toLocaleString()}
                        </p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-4 text-center">
                        <p className="text-xs sm:text-sm text-orange-600">
                          Adjustments
                        </p>
                        <p className="text-lg sm:text-2xl font-bold text-orange-700">
                          ₱
                          {parseFloat(billingDetails.total_adjustment).toFixed(
                            2,
                          ) || 0}
                        </p>
                      </div>
                      <div
                        className={`rounded-lg p-4 text-center ${billingDetails.outstanding_balance > 0 ? "bg-red-50" : "bg-emerald-50"}`}
                      >
                        <p
                          className={`text-xs sm:text-sm ${billingDetails.outstanding_balance > 0 ? "text-red-600" : "text-emerald-600"}`}
                        >
                          Outstanding Balance
                        </p>
                        <p
                          className={`text-lg sm:text-2xl font-bold ${billingDetails.outstanding_balance > 0 ? "text-red-700" : "text-emerald-700"}`}
                        >
                          ₱
                          {parseFloat(
                            billingDetails.outstanding_balance,
                          ).toFixed(2) || 0}
                        </p>
                      </div>
                    </div>

                    {/* Due Date & Disconnection */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <div className="flex-1 border border-slate-200 rounded-lg p-4">
                        <p className="text-sm text-slate-500">Due Date</p>
                        <p className="font-semibold text-slate-900">
                          {formatDate(billingDetails.current_due_date)}
                        </p>
                      </div>
                      {billingDetails.disconnection_date && (
                        <div className="flex-1 border border-red-200 bg-red-50 rounded-lg p-4">
                          <p className="text-sm text-red-600">
                            Disconnection Date
                          </p>
                          <p className="font-semibold text-red-700">
                            {formatDate(billingDetails.disconnection_date)}
                          </p>
                        </div>
                      )}
                      {billingDetails.is_disconnected && (
                        <div className="flex-1 border border-red-200 bg-red-50 rounded-lg p-4">
                          <p className="text-sm text-red-600">Status</p>
                          <p className="font-semibold text-red-700 flex items-center gap-2">
                            <WifiOff className="w-4 h-4" /> Disconnected
                          </p>
                        </div>
                      )}
                    </div>

                    <hr />

                    {(() => {
                      const latestSOA = getClientLatestSOA(
                        billingDetails.client.id,
                      );
                      if (latestSOA) {
                        return (
                          <>
                            <h2 className="font-semibold text-slate-900 mb-1 mt-4">
                              Payment Adjustment
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <div className="grid grid-cols-2 gap-2">
                                <div className="flex-1 border border-slate-200 rounded-sm p-1">
                                  <p className="font-semibold text-slate-900 ml-3 flex items-center gap-2">
                                    No. of Days
                                  </p>
                                  {/* <p className="text-sm text-slate-500">No. of Days</p> */}
                                </div>
                                <input
                                  min={0}
                                  max={30}
                                  type="number"
                                  disabled={!adjustmentButtonSaved}
                                  placeholder="Number of days"
                                  value={numDays}
                                  onChange={(e) => {
                                    let value = Number(e.target.value);

                                    if (e.target.value === "") {
                                      setNumDays("");
                                      return;
                                    }

                                    if (value < 0) value = 0;
                                    if (value > 30) value = 30;

                                    handlesetNumDays(value);
                                  }}
                                  className="border border-gray-300 rounded-sm px-2 w-full py-1"
                                />
                              </div>
                              <div className="flex justify-stretch gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="w-full"
                                  onClick={() =>
                                    handleAdjustmentButtonSaved(
                                      billingDetails.client.id,
                                      !adjustmentButtonSaved,
                                    )
                                  }
                                >
                                  {adjustmentButtonSaved ? (
                                    <Save className="w-4 h-4 mr-1" />
                                  ) : (
                                    <Edit className="w-4 h-4 mr-1" />
                                  )}

                                  <span className="px-2">
                                    {adjustmentButtonSaved
                                      ? "Save Adjustment"
                                      : "Edit Adjustment"}
                                  </span>
                                </Button>

                                {adjustmentButtonSaved && (
                                  <Button
                                    size="sm"
                                    variant="destructive"
                                    className="w-full"
                                    onClick={() => {
                                      setAdjustmentButtonSaved(false);
                                      // reset value if needed
                                      handlesetNumDays(numDaysOld);
                                      setNumDays(numDaysOld);
                                    }}
                                  >
                                    Cancel
                                  </Button>
                                )}
                              </div>
                            </div>
                            {billingDetails.outstanding_balance === 0 && (
                              <p className="text-sm text-red-600 mt-2">
                                Outstanding Balance is zero. When you save the
                                adjustment, the client's status will be updated
                                to "Paid" and add to your payment history.
                              </p>
                            )}
                            <hr className="mt-5" />
                          </>
                        );
                      }
                    })()}

                    {/* Payment History */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">
                        Recent Payments
                      </h4>
                      {billingDetails.payment_history?.length === 0 ? (
                        <p className="text-sm text-slate-500 text-center py-4">
                          No payment history
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {billingDetails.payment_history?.map((payment) => (
                            <div
                              key={payment.id}
                              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                            >
                              <div>
                                <p className="font-medium text-slate-900">
                                  ₱{payment.amount?.toLocaleString()}
                                </p>
                                <p className="text-xs text-slate-500">
                                  {payment.payment_method} •{" "}
                                  {payment.reference_number || "N/A"}
                                </p>
                              </div>
                              <p className="text-sm text-slate-500">
                                {formatDate(payment.created_at)}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* SOA Section */}
                    <div>
                      <h4 className="font-semibold text-slate-900 mb-3">
                        Statement of Account
                      </h4>
                      {(() => {
                        const latestSOA = getClientLatestSOA(
                          billingDetails.client.id,
                        );
                        if (
                          !latestSOA ||
                          billingDetails.client.status === "Paid"
                        ) {
                          return (
                            <div className="text-center py-4 border border-dashed border-slate-300 rounded-lg">
                              <FileText className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                              <p className="text-sm text-slate-500 mb-3">
                                No SOA generated yet
                              </p>
                              <Button
                                size="sm"
                                onClick={() =>
                                  generateSOA(billingDetails.client.id)
                                }
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                Generate SOA
                              </Button>
                            </div>
                          );
                        }
                        return (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <div>
                                <p className="font-semibold text-blue-900">
                                  {latestSOA.soa_number}
                                </p>
                                <p className="text-xs text-blue-600">
                                  Generated: {formatDate(latestSOA.created_at)}
                                </p>
                              </div>
                              <span
                                className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                  latestSOA.status === "paid"
                                    ? "bg-emerald-100 text-emerald-700"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                {latestSOA.status?.toUpperCase()}
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 mb-3 text-sm">
                              <div>
                                <span className="text-blue-600">Period:</span>
                                <span className="ml-1 text-blue-900">
                                  {latestSOA.billing_period_start} -{" "}
                                  {latestSOA.billing_period_end}
                                </span>
                              </div>
                              <div>
                                <span className="text-blue-600">Due:</span>
                                <span className="ml-1 text-blue-900">
                                  {latestSOA.due_date}
                                </span>
                              </div>
                              <div>
                                <span className="text-blue-600">
                                  Amount Due:
                                </span>
                                <span className="ml-1 text-blue-900 font-bold">
                                  ₱
                                  {parseFloat(
                                    latestSOA.total_amount_due,
                                  ).toFixed(2)}
                                </span>
                              </div>
                              <div>
                                <span className="text-blue-600">
                                  Email Sent:
                                </span>
                                <span className="ml-1 text-blue-900">
                                  {latestSOA.email_sent ? "✓ Yes" : "✗ No"}
                                </span>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  downloadSOAPDF(
                                    latestSOA.id,
                                    latestSOA.soa_number,
                                  )
                                }
                                className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100"
                                data-testid={`download-soa-${latestSOA.id}`}
                              >
                                <Download className="w-4 h-4 mr-1" />
                                Download PDF
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => resendSOAEmail(latestSOA.id)}
                                className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-100"
                                data-testid={`resend-soa-${latestSOA.id}`}
                              >
                                <Mail className="w-4 h-4 mr-1" />
                                Send Email
                              </Button>
                              {/* <Button
                                size="sm"
                                variant="outline"
                                onClick={() =>
                                  generateSOA(billingDetails.client.id)
                                }
                                className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                title="Generate New SOA"
                              >
                                <RefreshCw className="w-4 h-4" />
                              </Button> */}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Actions */}
                    {/* {billingDetails.outstanding_balance > 0 && (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => markAsPaid(billingDetails.client.id)}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Mark as Fully Paid (Zero Balance)
                    </Button>
                  )} */}
                  </div>
                )
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Record Payment Dialog */}
        <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
          <DialogContent className="p-0 overflow-hidden">
            <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-6">
              <form onSubmit={recordPayment} className="space-y-4">
                <div className="relative">
                  {loading && (
                    <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                      <div className="flex items-center gap-2 text-blue-700 font-semibold">
                        <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        Saving client...
                      </div>
                    </div>
                  )}

                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">
                      {client_payment_info?.full_name || "N/A"}
                    </h3>
                    <p className="text-sm text-slate-600">
                      Account: {client_payment_info?.account_number || "N/A"}
                    </p>
                    <p className="text-sm text-slate-600">
                      Plan: {client_payment_info?.plan_name || "N/A"} - ₱
                      {client_payment_info?.monthly_fee || "N/A"}/mo
                    </p>
                  </div>

                  <div>
                    <Label>Amount (₱)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={paymentData.amount}
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          amount: e.target.value,
                        })
                      }
                      required
                    />
                  </div>
                  <div>
                    <Label>Payment Method</Label>
                    <Select
                      value={paymentData.payment_method}
                      onValueChange={(val) =>
                        setPaymentData({ ...paymentData, payment_method: val })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cash">Cash</SelectItem>
                        <SelectItem value="gcash">GCash</SelectItem>
                        <SelectItem value="credit_card">Credit Card</SelectItem>
                        <SelectItem value="bank_transfer">
                          Bank Transfer
                        </SelectItem>
                        <SelectItem value="others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reference Number</Label>
                    <Input
                      value={paymentData.reference_number}
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          reference_number: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>
                  <div>
                    <Label>Notes</Label>
                    <Input
                      value={
                        paymentData.notes
                          ? paymentData.notes
                          : "Thank you for your payment!"
                      }
                      onChange={(e) =>
                        setPaymentData({
                          ...paymentData,
                          notes: e.target.value,
                        })
                      }
                      placeholder="Optional"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full mt-4  text-[20px] font-semibold h-14"
                  >
                    Record Payment
                  </Button>
                </div>
              </form>
            </div>
          </DialogContent>
        </Dialog>

        {/* Payment Proof Dialog */}
        <Dialog open={proofDialog} onOpenChange={setProofDialog}>
          <DialogContent className="max-w-6xl p-0 overflow-hidden">
            <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
              <DialogTitle>Payment Proof - {proofClientName}</DialogTitle>
            </DialogHeader>

            <div className="px-6 pb-6">
              <div className="relative overflow-hidden border rounded-lg">
                {proofImage && (
                  <img
                    src={
                      proofImage.startsWith("http") ||
                      proofImage.startsWith("https")
                        ? proofImage
                        : `${API_FOR_IMG}${proofImage}`
                    }
                    alt="Payment proof"
                    className="max-h-96 cursor-grab"
                    data-testid="proof-image"
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{
                      transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                      transition: dragging ? "none" : "transform 0.2s",
                    }}
                  />
                )}
              </div>

              <div className="flex gap-2 mt-3">
                <Button onClick={zoomIn} className="px-3 py-1rounded">
                  Zoom In
                </Button>
                <Button onClick={zoomOut} className="px-3 py-1 rounded">
                  Zoom Out
                </Button>
                <Button onClick={resetZoom} className="px-3 py-1 rounded">
                  Reset
                </Button>
              </div>
            </div>
            {/* <p className="text-sm text-slate-500 mt-4">
              Verify this payment proof before marking the client as paid.
            </p> */}
          </DialogContent>
        </Dialog>

        <Dialog
          open={reconnectDialog.open}
          onOpenChange={(val) =>
            setReconnectDialog({ open: val, client: null, extension_date: "" })
          }
        >
          <DialogContent className="p-0 overflow-hidden">
            {loading && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-sm z-50 flex items-center justify-center rounded-lg">
                <div className="flex items-center gap-2 text-blue-700 font-semibold">
                  <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                  Saving client...
                </div>
              </div>
            )}
            <DialogHeader className="top-0 z-20 bg-white px-6 py-4 border-b">
              <DialogTitle>Reconnect Client</DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-6">
              {reconnectDialog.client && (
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <h3 className="font-semibold text-slate-900 mb-2">
                      {reconnectDialog.client.full_name}
                    </h3>
                    <p className="text-sm text-slate-600">
                      Account: {reconnectDialog.client.account_number}
                    </p>
                    <p className="text-sm text-slate-600">
                      Plan: {reconnectDialog.client.plan_name} - ₱
                      {reconnectDialog.client.monthly_fee}/mo
                    </p>
                  </div>

                  {/* <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-sm text-slate-500">Client</p>
                  <p className="font-semibold text-slate-900">
                    {reconnectDialog.client.full_name}
                  </p>
                </div> */}

                  <div>
                    <Label>Extension Date</Label>

                    <Popover open={dateOpen} onOpenChange={setDateOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-between text-left text-[15px] font-semibold h-10 mt-2"
                        >
                          {reconnectDialog.extension_date
                            ? format(
                                new Date(reconnectDialog.extension_date),
                                "PPP",
                              )
                            : "Select extension date"}

                          <CalendarIcon className="w-4 h-6 text-slate-500" />
                        </Button>
                      </PopoverTrigger>

                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={
                            reconnectDialog.extension_date
                              ? new Date(reconnectDialog.extension_date)
                              : null
                          }
                          onSelect={(date) => {
                            if (!date) return; // ✅ prevent error

                            setReconnectDialog({
                              ...reconnectDialog,
                              extension_date: format(date, "yyyy-MM-dd"),
                            });

                            setDateOpen(false); // 👈 close picker
                          }}
                          disabled={(date) => date < new Date()} // prevent past dates
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <Button
                    className="w-full bg-green-600 hover:bg-green-700 text-[20px] font-semibold h-14"
                    onClick={async () => {
                      showLoader("Reconnecting client...");
                      try {
                        const token = localStorage.getItem("token");

                        await axios.post(
                          `${API}/reconnect-client/${reconnectDialog.client.id}`,
                          {
                            extension_date: reconnectDialog.extension_date,
                          },
                          {
                            headers: {
                              Authorization: `Bearer ${token}`,
                            },
                          },
                        );

                        // hideLoader();
                        // toast.success("Client reconnected with extension date");
                        setReconnectDialog({
                          open: false,
                          client: null,
                          extension_date: "",
                        });

                        fetchClients();
                        showSuccess("Client reconnected successfully");
                      } catch (error) {
                        hideLoader();
                        toast.error(
                          error.response?.data?.detail || "Reconnect failed",
                        );
                      }
                    }}
                  >
                    Confirm Reconnect
                  </Button>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {dialog.show && (
          <ConfirmDialog
            title={
              dialog.action === "send_reminders"
                ? "Send Payment Reminders?"
                : dialog.action === "disconnect"
                  ? "Disconnect Client?"
                  : dialog.action === "reconnect"
                    ? "Reconnect Client?"
                    : "Confirm Action"
            }
            message={
              dialog.action === "send_reminders"
                ? `Are you sure you want to send payment reminders to ${dialog.client.full_name}?`
                : dialog.action === "disconnect"
                  ? `Are you sure you want to disconnect ${dialog.client.full_name}?`
                  : dialog.action === "reconnect"
                    ? `Are you sure you want to reconnect ${dialog.client.full_name}?`
                    : ""
            }
            confirmText={
              dialog.action === "send_reminders"
                ? "Send Reminders"
                : dialog.action === "disconnect"
                  ? "Disconnect"
                  : dialog.action === "reconnect"
                    ? "Reconnect"
                    : dialog.action.charAt(0).toUpperCase() +
                      dialog.action.slice(1)
            }
            color={
              dialog.action === "disconnect"
                ? "red"
                : dialog.action === "reconnect"
                  ? "green"
                  : "blue"
            }
            icon={
              dialog.action === "send_reminders"
                ? Send
                : dialog.action === "disconnect"
                  ? WifiOff
                  : dialog.action === "reconnect"
                    ? WifiSync
                    : CheckCircle
            }
            onConfirm={handleConfirm}
            onCancel={() =>
              setDialog({ show: false, action: "", client: null })
            }
          />
        )}
      </div>
    </AdminLayout>
  );
}
